/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/resourceItem
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { suppressKeyboardEventDisableEnterOnEdit } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { TextWithMetadata } from 'labstep-web/core/DataGrid/Text/WithMetadata';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { TextWithIcon } from 'labstep-web/core/Text/WithIcon';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { getProtocolConditionResourceItemStatusSelectProps } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/utils';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import {
  ResourceItem,
  type ResourceItemStatus,
  ResourceItemStatusValues,
} from 'labstep-web/models/resource-item.model';
import styles from './styles.module.scss';

export const getProtocolValueResourceItemStatusColDef = (
  experimentOrProtocol: Experiment | Protocol,
  name: string,
): SmartTableColDef => ({
  colId: `protocol_value:${name}:resource_item_status`,
  headerName: 'Status',
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: {
    toolPanelColumnName: `Status - ${name}`,
    icon: 'info circle',
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolConditionService.getProtocolValueEditable(
      params,
      experimentOrProtocol,
      name,
    ),
  suppressKeyboardEvent: suppressKeyboardEventDisableEnterOnEdit,
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) ||
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    if (protocolValue?.resourceItem?.status) {
      return protocolValue?.resourceItem?.status;
    }
    return '';
  },
  valueSetter: (params: ValueSetterParams) => {
    if (!params.node || !params.newValue) {
      return false;
    }
    const newValue = params.newValue.toString().toLowerCase();

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) ||
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    if (!variableTemplate && !protocolValue?.resourceItem) {
      return false;
    }

    if (Object.keys(ResourceItemStatusValues).includes(newValue)) {
      return GridSmartTableProtocolConditionService.updateEntity(
        params,
        ResourceItem.entityName,
        protocolValue!.resourceItem!.idAttr,
        {
          status: newValue as ResourceItemStatus,
        },
      );
    }
    return false;
  },

  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (!params.data) {
      return '';
    }

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) ||
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    if (!protocolValue || !protocolValue?.resourceItem) {
      return null;
    }

    const resourceItem = protocolValue.resourceItem;

    return (
      <TextWithMetadata
        text={
          <Flex className={styles.status}>
            <TextWithIcon
              icon="circle"
              iconProps={{ className: styles[resourceItem.status] }}
              text={
                ResourceItemStatusValues[
                  resourceItem.status as keyof typeof ResourceItemStatusValues
                ]
              }
            />
            <Icon
              name="sort down"
              onClick={() =>
                params.api.startEditingCell({
                  rowIndex: params.rowIndex,
                  colKey: 'status',
                })
              }
            />
          </Flex>
        }
      />
    );
  },
  cellEditor: DataGrid?.CellEditor?.Select,
  cellEditorPopup: true,
  cellEditorParams: (params: CellEditorParams<ProtocolCondition>) => {
    if (!params.node) {
      return false;
    }

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) ||
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    if (
      (!protocolValue && !variableTemplate) ||
      !protocolValue?.resourceItem
    ) {
      return false;
    }

    const onPropsChange = (option: { value: string }) => {
      GridSmartTableProtocolConditionService.updateEntity(
        params,
        ResourceItem.entityName,
        protocolValue!.resourceItem!.idAttr,
        {
          status: option.value as ResourceItemStatus,
        },
      );
    };

    return {
      props: getProtocolConditionResourceItemStatusSelectProps(
        protocolValue.resourceItem,
        onPropsChange,
      ),
    };
  },
  cellStyle: (params) =>
    GridSmartTableProtocolConditionService.getCellStyle(
      params,
      experimentOrProtocol,
      ProtocolValue.entityName,
      name,
    ),
});
